import isEmpty from 'lodash/isEmpty';
import { nanoid } from 'nanoid';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';

import { useCookieWatcher } from '@sravni/react-utils';
import { open } from '@sravni/vitrina/lib/helpers/navigationHelper';
import { replaceTemplate } from '@sravni/vitrina/lib/helpers/replaceHelper';

import { MONETIZATION_TYPES } from '@components/RatingTable/components/ProductPopup/constants/advertising';
import { ONLINE_LOAN_APPLICATION_URL } from '@components/RatingTable/components/ProductPopup/constants/urls';
import ProductTypes from '@components/RatingTable/components/ProductPopup/enums/productTypes';
import { usePaymentNetwork } from '@components/RatingTable/components/ProductPopup/hooks/usePaymentNetwork';

import { useProductConfig } from '../../../../../store/hooks/productPopupInfo';
import { useUser } from '../../../../../store/hooks/users';
import type { IMakePixelProps, IPixelHandleEventProps } from '../helpers/pixelHelper';
import { makePixelLink } from '../helpers/pixelHelper';
import { toString } from '../helpers/queryParamsToString';

const ANALYTIC_PATHNAME = 'analytic_path';

export const usePixelMaker = () => {
  const config = useProductConfig();
  const [utmz] = useCookieWatcher('__utmz');
  const [ym] = useCookieWatcher('_ym_uid');
  const [ga] = useCookieWatcher('_ga');
  const { asPath, query } = useRouter();
  const pathForPixel = (query[ANALYTIC_PATHNAME] as string) || asPath;

  const wl = false;
  const subId = useUser();
  const { current: sub } = React.useRef(nanoid());

  return useCallback(
    (link?: string, props?: IMakePixelProps) => {
      if (!link) {
        return null;
      }

      if (props.isConversion) {
        return makePixelLink(link, props);
      }

      return makePixelLink(link, {
        ...props,
        type: props.type || config.eventCategory,
        isWL: Boolean(wl),
        sub,
        pathname: pathForPixel,
        userId: subId?.sub,
        cookies: {
          utmz,
          ym,
          ga,
        },
      });
    },
    [config.eventCategory, wl, sub, subId?.sub, utmz, ym, ga, pathForPixel],
  );
};

export const usePixel = (link?: string, props?: IMakePixelProps): string | null => {
  const make = usePixelMaker();

  return useMemo(() => make(link, props), [link, props, make]);
};

export const usePixelHandleEvent = (props: IPixelHandleEventProps) => {
  const { current: sub } = React.useRef(nanoid());
  const {
    product,
    advertising: { monetization: { kind = '', pixels: { click = '' } = {} } = {} } = {},
    isAdvertisingCommon,
    productLink,
    source,
    target = '_blank',
    productId,
    productType,
    filters,
    freeButtonAction = {},
    onEvent,
  } = props || {};
  const { paymentNetwork } = usePaymentNetwork(product);

  const link = useMemo(() => {
    const isLongRequest = kind === MONETIZATION_TYPES.REQUEST_LONG;

    if (!isLongRequest || productType !== ProductTypes.CreditCards) {
      return click;
    }

    const queryParams =
      'href' in freeButtonAction
        ? Object.entries(freeButtonAction.queryParams)?.reduce((out, [key, { template, properties }]) => {
            out[key] = replaceTemplate(template, properties, { productId });

            return out;
          }, {} as Record<string, string>)
        : {};

    return `${ONLINE_LOAN_APPLICATION_URL}${!isEmpty(queryParams) ? toString(queryParams) : ''}`;
  }, [kind, freeButtonAction, productType, click, productId]);

  const href = usePixel(link, {
    product,
    productLink: isAdvertisingCommon ? productLink : undefined,
    sub,
    source,
    kind,
    productId,
    productType,
    filters,
    paymentNetwork,
  });

  const handleEvent = useCallback(() => {
    onEvent();
    open({ href, target });
  }, [href, target, onEvent]);

  if (isEmpty(props?.advertising)) {
    return null;
  }

  return handleEvent;
};
